// src/components/condos/CondoViewPage.tsx
// Read-only condo detail page (user-facing)

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { condoService } from '../../services/condoService';
import { useAuth } from '../../hooks/useAuth';
import { useCondos } from '../../hooks/useCondos';
import { Condo } from '../../types/condo';
import BackgroundShapes from '../common/BackgroundShapes';
import WaitlistModal from './WaitlistModal';
import SignUpPromptModal from '../common/SignUpPromptModal';

const CondoViewPage: React.FC = () => {
  const { condoId } = useParams<{ condoId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [condo, setCondo] = useState<Condo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const { getWaitlistUsers, joinWaitlist, leaveWaitlist } = useCondos();

  useEffect(() => {
    if (!condoId) return;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await condoService.getCondoById(condoId);
        if (res.success && res.data?.condo) {
          setCondo(res.data.condo);
        } else {
          setError('Failed to load condo');
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [condoId]);

  const loadCondo = async () => {
    if (!condoId) return;
    try {
      const res = await condoService.getCondoById(condoId);
      if (res.success && res.data?.condo) setCondo(res.data.condo);
    } catch {
      // ignore
    }
  };

  const handleBack = () => navigate(-1);

  const handleShare = () => {
    const url = condo?.shortId
      ? `${window.location.origin}/c/${condo.shortId}`
      : window.location.href;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCheckWaitlist = () => {
    if (!isAuthenticated) {
      setShowSignUpModal(true);
      return;
    }
    setWaitlistModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper relative">
        <BackgroundShapes />
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-coffee text-xl">
            Loading condo...
          </div>
        </div>
      </div>
    );
  }

  if (error || !condo) {
    return (
      <div className="min-h-screen bg-paper relative">
        <BackgroundShapes />
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-red-600 mb-4">{error || 'Condo not found'}</p>
            <button
              onClick={() => navigate('/condos')}
              className="bg-terracotta text-white px-4 py-2 rounded-lg"
            >
              Back to Condos
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sourceLabels: Record<string, string> = {
    streeteasy: 'StreetEasy',
    zillow: 'Zillow',
    'apartments.com': 'Apartments.com',
  };
  const sourceLabel = condo.source ? (sourceLabels[condo.source] ?? condo.source) : null;

  const locationText =
    [condo.location?.neighborhood, condo.location?.borough].filter(Boolean).join(', ') ||
    condo.location?.borough ||
    '—';
  const priceText = condo.price != null ? `$${condo.price.toLocaleString()} / mo` : '—';
  const bedroomsText = condo.bedrooms === 0 ? 'Studio' : condo.bedrooms != null ? `${condo.bedrooms} bed` : null;
  const bathroomsText = condo.bathrooms != null ? `${condo.bathrooms} bath` : null;
  const sqftText = condo.sqft != null ? `${condo.sqft.toLocaleString()} sqft` : null;
  const unitDetailText = [bedroomsText, bathroomsText, sqftText].filter(Boolean).join(' · ');
  const images = condo.images && condo.images.length > 0 ? condo.images : [];
  const primaryImage = images[0] || null;

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Back button */}
          <button
            onClick={handleBack}
            className="mb-6 bg-cream border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-white transition-colors text-sm font-medium"
          >
            ← Back
          </button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-coffee mb-2">{condo.name}</h1>
            <p className="text-coffee opacity-80">
              {locationText}
              {priceText && ` · ${priceText}`}
            </p>
          </div>

          {/* Images */}
          <div className="mb-8 rounded-lg overflow-hidden border-2 border-coffee">
            {primaryImage ? (
              <img
                src={primaryImage}
                alt={condo.name}
                className="w-full h-64 md:h-80 object-cover"
              />
            ) : (
              <div className="w-full h-64 md:h-80 bg-cream flex items-center justify-center">
                <span className="text-coffee/50 text-lg font-medium">No Image</span>
              </div>
            )}
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2 p-2 bg-cream">
                {images.slice(0, 4).map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt=""
                    className="w-full h-20 object-cover rounded border border-coffee"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-6 mb-8 space-y-6">
            <div>
              <h3 className="text-coffee font-semibold mb-2">Location</h3>
              <p className="text-coffee">{locationText}</p>
              {condo.location?.address && (
                <p className="text-coffee/70 text-sm mt-1">{condo.location.address}</p>
              )}
            </div>

            <div>
              <h3 className="text-coffee font-semibold mb-2">Price</h3>
              <p className="text-coffee">{priceText}</p>
            </div>

            {unitDetailText && (
              <div>
                <h3 className="text-coffee font-semibold mb-2">Unit Details</h3>
                <p className="text-coffee">{unitDetailText}</p>
              </div>
            )}

            {condo.details?.amenities && condo.details.amenities.length > 0 && (
              <div>
                <h3 className="text-coffee font-semibold mb-2">Amenities</h3>
                <p className="text-coffee">{condo.details.amenities.join(', ')}</p>
              </div>
            )}

            {condo.details?.description && (
              <div>
                <h3 className="text-coffee font-semibold mb-2">Description</h3>
                <p className="text-coffee whitespace-pre-wrap">{condo.details.description}</p>
              </div>
            )}

            {condo.waitlistCount != null && (
              <div>
                <h3 className="text-coffee font-semibold mb-2">Interest Group</h3>
                <p className="text-coffee">{condo.waitlistCount} in interest group</p>
              </div>
            )}

            {!!condo.sourceUrl && (
              <div>
                <h3 className="text-coffee font-semibold mb-2">Listing Info</h3>
                <p className="text-coffee text-sm">
                  Source:{' '}
                  <a
                    href={condo.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-steel hover:text-coffee"
                  >
                    {sourceLabel ?? condo.source}
                  </a>
                </p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleCheckWaitlist}
              className="bg-terracotta text-white border-2 border-coffee px-6 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-colors"
            >
              Interest Group
            </button>
            <button
              onClick={handleShare}
              className="bg-cream border-2 border-coffee text-coffee px-6 py-2 rounded-lg font-semibold hover:bg-white transition-colors"
            >
              {copied ? 'Copied!' : 'Share'}
            </button>
          </div>

          <WaitlistModal
            condoId={condoId ?? ''}
            condo={condo}
            condoName={condo.name}
            isOpen={waitlistModalOpen}
            onClose={() => setWaitlistModalOpen(false)}
            onFetchWaitlistUsers={getWaitlistUsers}
            onJoinWaitlist={joinWaitlist}
            onLeaveWaitlist={leaveWaitlist}
            onRefresh={loadCondo}
          />
          <SignUpPromptModal
            isOpen={showSignUpModal}
            onClose={() => setShowSignUpModal(false)}
            message="Sign up to join the interest group"
            condoId={condoId ?? ''}
          />
        </div>
      </div>
    </div>
  );
};

export default CondoViewPage;
